import {
    ArrowDownToLine,
    ArrowUpFromLine,
    CheckCircle2,
    ListChecks,
} from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { SocialLoginButtons } from '@/features/auth';

interface FundingStepProps {
    onDeposit: () => void;
    /**
     * The account has no social login yet. Offering one here is the only place a
     * wallet-first user is told it is possible at all, and it is strictly
     * optional: finishing onboarding never waits on it.
     */
    canAddSocialLogin?: boolean;
}

const POINTS: {
    icon: React.ComponentType<{ size?: number; className?: string }>;
    title: string;
    description: string;
}[] = [
    {
        icon: ArrowDownToLine,
        title: 'Deposits',
        description:
            'Deposits bridge USDC from your Cronos wallet, keep a little CRO for gas.',
    },
    {
        icon: ArrowUpFromLine,
        title: 'Withdrawals',
        description:
            'Withdraw any time, USDC returns to the same Cronos wallet you connected.',
    },
    {
        icon: ListChecks,
        title: 'Live status',
        description:
            'Deposits and withdrawals track their progress in the wallet panel until settled.',
    },
];

/** Step 4 — the "you're all set" card: how money moves in and out. */
const FundingStep: React.FC<FundingStepProps> = ({
    onDeposit,
    canAddSocialLogin = false,
}) => (
    <div className="flex flex-col items-center px-6 pb-6 pt-2 text-center">
        <div className="flex size-16 items-center justify-center rounded-2xl border border-success/30 bg-success/5">
            <CheckCircle2 size={30} className="text-success" />
        </div>

        <h2 className="mt-4 text-xl font-bold tracking-tight text-foreground">
            You&apos;re all set!
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
            Here&apos;s how money moves in and out of Snappy:
        </p>

        <div className="mt-6 w-full divide-y divide-border rounded-xl border border-border text-left">
            {POINTS.map(({ icon: Icon, title, description }) => (
                <div key={title} className="flex items-center gap-3 p-3.5">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-secondary/40">
                        <Icon size={18} className="text-foreground/80" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                            {title}
                        </p>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                            {description}
                        </p>
                    </div>
                </div>
            ))}
        </div>

        <Button
            size="lg"
            className="mt-6 w-full font-semibold"
            onClick={onDeposit}
        >
            Deposit now
        </Button>

        {canAddSocialLogin && (
            <section
                aria-labelledby="funding-add-social"
                className="mt-6 w-full rounded-xl border border-border p-3.5 text-left"
            >
                <p
                    id="funding-add-social"
                    className="text-sm font-semibold text-foreground"
                >
                    Add a social login (optional)
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    Sign in later without your wallet. The login is added to the
                    account you just set up, which your wallet keeps owning.
                </p>
                <SocialLoginButtons
                    layout="providerGrid"
                    className="mt-3"
                    onError={(message) => toast.error(message)}
                />
            </section>
        )}
    </div>
);

export default FundingStep;
